import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { apiFetch } from '../api.js';
import { useAuth } from './AuthContext.jsx';
import { useFamily } from './FamilyContext.jsx';

const RecoveryEmailPromptContext = createContext(null);

const AUTO_PROMPT_THRESHOLD = 10;
const AUTO_PROMPT_STORAGE_KEY = 'ptcmRecoveryEmailAutoPromptSeen';
const ENGAGED_STORAGE_KEY = 'ptcmRecoveryReminderEngaged';

function getStoredMap(storageKey) {
  if (typeof window === 'undefined') return {};

  try {
    const raw = window.localStorage.getItem(storageKey);
    return raw ? JSON.parse(raw) : {};
  } catch (error) {
    return {};
  }
}

function setStoredFlag(storageKey, memberUuid) {
  if (typeof window === 'undefined' || !memberUuid) return;

  const next = {
    ...getStoredMap(storageKey),
    [memberUuid]: true
  };

  window.localStorage.setItem(storageKey, JSON.stringify(next));
}

function hasStoredAutoPromptBeenSeen(memberUuid) {
  if (!memberUuid) return false;
  return Boolean(getStoredMap(AUTO_PROMPT_STORAGE_KEY)[memberUuid]);
}

function hasStoredEngagement(memberUuid) {
  if (!memberUuid) return false;
  return Boolean(getStoredMap(ENGAGED_STORAGE_KEY)[memberUuid]);
}

export function RecoveryEmailPromptProvider({ children }) {
  const { token, user, refreshUser } = useAuth();
  const { members, refresh } = useFamily();

  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [lastPromptSource, setLastPromptSource] = useState('manual');
  const [lastAnsweredQuestionsCount, setLastAnsweredQuestionsCount] = useState(0);
  const [optimisticEmail, setOptimisticEmail] = useState('');
  const [hasEngaged, setHasEngaged] = useState(false);

  const currentMember = useMemo(
    () => (members || []).find((member) => member.uuid === user?.uuid) || null,
    [members, user?.uuid]
  );
  const promptIdentityKey = currentMember?.uuid || user?.uuid || '';

  const savedEmail = useMemo(
    () => String(optimisticEmail || currentMember?.email || user?.email || '').trim(),
    [currentMember?.email, optimisticEmail, user?.email]
  );
  const emailVerifiedAt = currentMember?.email_verified_at || user?.email_verified_at || null;
  const recoveryEmailStatus = useMemo(() => {
    if (!savedEmail) return 'missing';
    return emailVerifiedAt ? 'verified' : 'pending';
  }, [emailVerifiedAt, savedEmail]);

  const canRegisterRecoveryEmail = Boolean(
    token &&
    user &&
    currentMember &&
    recoveryEmailStatus !== 'verified'
  );
  const canAutoPromptForRecoveryEmail = canRegisterRecoveryEmail && recoveryEmailStatus === 'missing';
  const isPendingRecoveryEmailVerification = recoveryEmailStatus === 'pending';

  useEffect(() => {
    if (!savedEmail) return;
    setOptimisticEmail(savedEmail);
  }, [savedEmail]);

  useEffect(() => {
    setIsOpen(false);
    setOptimisticEmail('');
    setSubmitError('');
    setHasEngaged(hasStoredEngagement(promptIdentityKey));
  }, [promptIdentityKey]);

  // The passive Topbar reminder for a *missing* recovery email should not nag during the
  // very first steps (profile setup, "questionnaire ready"). We only surface it once the
  // member has actually started answering, or as soon as an email is pending verification.
  const showRecoveryReminder = Boolean(
    canRegisterRecoveryEmail && (recoveryEmailStatus === 'pending' || hasEngaged)
  );

  const openPrompt = useCallback((source = 'manual', answeredQuestionsCount = 0) => {
    setLastPromptSource(source);
    setLastAnsweredQuestionsCount(answeredQuestionsCount);
    setSubmitError('');
    setIsOpen(true);
  }, []);

  const closePrompt = useCallback(() => {
    setIsOpen(false);
    setSubmitError('');
  }, []);

  const maybeAutoOpenPrompt = useCallback(({ answeredQuestionsCount, source = 'auto_threshold' }) => {
    const memberUuid = currentMember?.uuid || user?.uuid;

    // Record engagement (first answer) so the passive reminder can appear, even when the
    // full auto-prompt threshold isn't reached yet.
    if (answeredQuestionsCount >= 1 && memberUuid && !hasStoredEngagement(memberUuid)) {
      setStoredFlag(ENGAGED_STORAGE_KEY, memberUuid);
      setHasEngaged(true);
    }

    if (!canAutoPromptForRecoveryEmail || answeredQuestionsCount < AUTO_PROMPT_THRESHOLD) {
      return false;
    }

    if (!memberUuid || hasStoredAutoPromptBeenSeen(memberUuid)) {
      return false;
    }

    setStoredFlag(AUTO_PROMPT_STORAGE_KEY, memberUuid);
    openPrompt(source, answeredQuestionsCount);
    return true;
  }, [canAutoPromptForRecoveryEmail, currentMember?.uuid, openPrompt, user?.uuid]);

  const submitRecoveryEmail = useCallback(async ({ email, source }) => {
    const normalizedEmail = String(email || '').trim().toLowerCase();

    if (!normalizedEmail) {
      const error = new Error("L'adresse email est obligatoire.");
      setSubmitError(error.message);
      throw error;
    }

    setIsSubmitting(true);
    setSubmitError('');

    try {
      const payload = await apiFetch('/api/family/recovery-email', {
        token,
        method: 'POST',
        body: {
          email: normalizedEmail,
          source: source || lastPromptSource,
          answered_questions_count: lastAnsweredQuestionsCount
        }
      });

      setOptimisticEmail(payload?.member?.email || payload?.user?.email || payload?.email || normalizedEmail);

      await Promise.allSettled([
        refresh(),
        refreshUser()
      ]);

      return payload;
    } catch (error) {
      setSubmitError(error?.message || "Impossible d'enregistrer cet email.");
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  }, [lastAnsweredQuestionsCount, lastPromptSource, refresh, refreshUser, token]);

  const value = useMemo(() => ({
    autoPromptThreshold: AUTO_PROMPT_THRESHOLD,
    canRegisterRecoveryEmail,
    showRecoveryReminder,
    closePrompt,
    currentMember,
    emailVerifiedAt,
    isOpen,
    isSubmitting,
    isPendingRecoveryEmailVerification,
    openPrompt,
    recoveryEmailStatus,
    savedEmail,
    submitError,
    submitRecoveryEmail,
    maybeAutoOpenPrompt
  }), [
    canRegisterRecoveryEmail,
    showRecoveryReminder,
    closePrompt,
    currentMember,
    emailVerifiedAt,
    isOpen,
    isSubmitting,
    isPendingRecoveryEmailVerification,
    openPrompt,
    recoveryEmailStatus,
    savedEmail,
    submitError,
    submitRecoveryEmail,
    maybeAutoOpenPrompt
  ]);

  return (
    <RecoveryEmailPromptContext.Provider value={value}>
      {children}
    </RecoveryEmailPromptContext.Provider>
  );
}

export function useRecoveryEmailPrompt() {
  const context = useContext(RecoveryEmailPromptContext);

  if (!context) {
    throw new Error('useRecoveryEmailPrompt must be used within RecoveryEmailPromptProvider');
  }

  return context;
}
